import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { BarbadosGameState } from '../../types';
import { selectActionBarbados } from '../moves/selectActionsBarbados';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 3: アクション選択フェーズ（Barbados固有）
 *
 * ソロプレイヤーは4種類のアクションから1つを選択します。
 * アクションマーカー制限により、全4種を使用するまで同じアクションを再選択できません。
 *
 * @see docs/specifications/logic/barbados/phase_03_select_actions_override.md
 */
export const selectActionsBarbadosPhase: PhaseConfig<BarbadosGameState> = {
  moves: {
    selectAction: selectActionBarbados,
    undoToTurnStart,
  },

  // Phase開始時の処理: アクションマーカーリセット判定
  onBegin: ({ G, ctx }: { G: BarbadosGameState; ctx: any }) => {
    // アクションマーカーが未初期化の場合は初期化
    if (!G.actionMarkers) {
      G.actionMarkers = {
        ENGINEER: false,
        LOCOMOTIVE: false,
        URBANIZATION: false,
        PRODUCTION: false
      };
    }

    // 全4種類にマーカーが配置されているかチェック
    const allUsed = Object.values(G.actionMarkers).every(used => used === true);

    if (allUsed) {
      // 全マーカーをリセット
      G.actionMarkers = {
        ENGINEER: false,
        LOCOMOTIVE: false,
        URBANIZATION: false,
        PRODUCTION: false
      };

      G.actionMarkerResetTurn = ctx.turn;
      debug.log(`[selectActionsBarbadosPhase] All action markers reset at turn ${ctx.turn}`);
    }
  },

  // Phase終了条件: ソロプレイヤーがアクション選択完了、または Undo 強制遷移
  endIf: ({ G }: { G: BarbadosGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log(`[selectActionsBarbadosPhase] Force transition to issueShares (Undo)`);
      return true;
    }

    // Barbados Solo Play: プレイヤーは常に'0'
    const soloPlayer = G.players['0'];
    const isComplete = soloPlayer && soloPlayer.selectedAction !== null;

    if (isComplete) {
      debug.log(`[selectActionsBarbadosPhase] Phase ending, selected action: ${soloPlayer.selectedAction}`);
    }

    return isComplete;
  },

  // Phase終了時の処理
  onEnd: () => {
    debug.log(`[selectActionsBarbadosPhase] Phase ended, transitioning to buildTrack`);
  },

  // Phase 4（線路建設）へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: BarbadosGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'buildTrack';
  }
};
